import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Sparkles, Eye, Award, ShieldCheck, HeartHandshake, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { Hero } from "@/components/seo/SeoLayout";
import Breadcrumbs from "@/components/Breadcrumbs";

const CATEGORIES = ["Fashion", "Beauty & Personal Care", "Home & Kitchen", "Electronics", "Food & Beverage", "Baby & Kids", "Fitness", "Office & Stationery", "Other"];
const PRODUCT_COUNTS = ["1–10", "10–50", "50–200", "200–1,000", "1,000+"];

const BENEFITS = [
  { icon: <ShieldCheck className="h-5 w-5" />, t: "Credible Sustainability Score", d: "Turn your sustainability efforts into a transparent, easy-to-understand EcoWallet Score backed by structured evidence and a consistent evaluation methodology." },
  { icon: <Eye className="h-5 w-5" />, t: "Get Discovered by Conscious Consumers", d: "Put your products in front of consumers actively looking for better, more responsible choices." },
  { icon: <Sparkles className="h-5 w-5" />, t: "Premium Visibility", d: "Unlock opportunities for enhanced product placement and premium visibility across the EcoWallet ecosystem." },
  { icon: <Award className="h-5 w-5" />, t: "Reward Consumer Choice", d: "Make sustainability more actionable by giving consumers meaningful rewards for choosing products with stronger sustainability credentials." },
  { icon: <HeartHandshake className="h-5 w-5" />, t: "Build Trust Through Transparency", d: "Help customers understand what makes your products different through evidence-backed sustainability information." },
];

const emptyForm = {
  brand_name: "", contact_name: "", work_email: "", phone: "", website: "",
  category: CATEGORIES[0], product_count: PRODUCT_COUNTS[1], location: "",
  brand_description: "", sustainability_focus: "", social_url: "",
  certifications: "", sustainability_report_url: "", consent: false,
};

const ForBrandsPage = () => {
  useSEO({
    title: "For Sustainable Brands — Join Ecowallet",
    description: "Join Ecowallet's brand waitlist. Turn your sustainability efforts into a credible EcoWallet Score, get discovered by conscious consumers, and reward customers for choosing you.",
    keywords: "sustainable brand onboarding, sustainable brand partnership, sustainable brand waitlist, sustainable brand discovery",
  });
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the contact consent to continue.");
    if (form.brand_description.length < 20) return toast.error("Brand description needs at least 20 characters.");
    if (form.sustainability_focus.length < 20) return toast.error("Sustainability focus needs at least 20 characters.");
    setLoading(true);
    try {
      await api.post("/brands/waitlist", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center" data-testid="brand-waitlist-confirmation">
      <div className="grid place-items-center h-20 w-20 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 mx-auto">
        <CheckCircle2 className="h-10 w-10" />
      </div>
      <h1 className="mt-6 text-4xl font-bold tracking-tight">You're on the list! 🌱</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">
        Thanks for your interest in joining Ecowallet.<br />
        We're building a community of brands that believe better choices should be easier to discover — and more rewarding to make.<br />
        We'll be in touch when onboarding opens.
      </p>
      <Link to="/products" className="btn-pill bg-primary text-primary-foreground mt-8 inline-flex">
        Explore Ecowallet <ArrowRight className="h-4 w-4 ml-1" />
      </Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
      <Breadcrumbs items={[{ label: "For Brands" }]} />
      <Hero
        kicker="For Sustainable Brands"
        title={<>Your sustainability<br/>deserves to be seen.</>}
        subtitle="Join Ecowallet and help conscious consumers discover your brand, understand your sustainability efforts and choose you with confidence."
        primary={{ to: "#waitlist", label: "Join the Waitlist", testId: "brands-waitlist-cta" }}
        secondary={{ to: "#benefits", label: "Explore Brand Benefits" }}
      />

      {/* Benefits */}
      <section id="benefits" className="mt-20">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Why join Ecowallet?</div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Better choices deserve better rewards — and so do the brands behind them.</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {BENEFITS.map((b) => (
            <motion.div key={b.t} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="card-premium p-6" data-testid={`benefit-${b.t.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="grid place-items-center h-11 w-11 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">{b.icon}</div>
              <h3 className="mt-4 text-xl font-bold">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust note */}
      <section className="mt-14 card-premium p-6 sm:p-8 border-l-4 border-primary">
        <div className="text-xs uppercase tracking-widest text-primary font-semibold">Independence & transparency</div>
        <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
          Ecowallet's Sustainability Score is determined independently of paid visibility. Premium placement can improve
          discovery, but it does <strong>not</strong> influence a product's sustainability score. Ecowallet evaluates
          publicly available information using its sustainability assessment methodology.
        </p>
      </section>

      {/* Waitlist CTA + Form */}
      <section id="waitlist" className="mt-16 card-premium p-6 sm:p-10">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Be among the first brands on Ecowallet.</h2>
          <p className="text-muted-foreground mt-3">Join the waitlist and we'll be in touch when brand onboarding opens.</p>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="brand-waitlist-form">
          <Field label="Brand name *" value={form.brand_name} onChange={(v) => setForm({ ...form, brand_name: v })} required />
          <Field label="Contact person *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} required />
          <Field label="Work email *" type="email" value={form.work_email} onChange={(v) => setForm({ ...form, work_email: v })} required />
          <Field label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          <Field label="Brand website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://..." />
          <Select label="Product category *" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={CATEGORIES} />
          <Select label="Number of products *" value={form.product_count} onChange={(v) => setForm({ ...form, product_count: v })} options={PRODUCT_COUNTS} />
          <Field label="City / Country *" value={form.location} onChange={(v) => setForm({ ...form, location: v })} required />
          <TextArea label="Brand description * (min 20 chars)" value={form.brand_description} onChange={(v) => setForm({ ...form, brand_description: v })} required rows={3} />
          <TextArea label="Sustainability focus * (min 20 chars)" value={form.sustainability_focus} onChange={(v) => setForm({ ...form, sustainability_focus: v })} required rows={3} placeholder="Materials, certifications, packaging, supply chain..." />
          <Field label="Instagram / social (optional)" value={form.social_url} onChange={(v) => setForm({ ...form, social_url: v })} />
          <Field label="Existing certifications (optional)" value={form.certifications} onChange={(v) => setForm({ ...form, certifications: v })} placeholder="GOTS, USDA Organic, B Corp..." />
          <Field label="Sustainability report URL (optional)" value={form.sustainability_report_url} onChange={(v) => setForm({ ...form, sustainability_report_url: v })} col={2} />

          <label className="col-span-full flex items-start gap-3 mt-2 cursor-pointer" data-testid="brand-consent">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 rounded border-border accent-primary" data-testid="brand-consent-checkbox" required />
            <span className="text-xs text-muted-foreground">I agree to be contacted by Ecowallet regarding brand onboarding and partnership opportunities.</span>
          </label>
          <button type="submit" disabled={loading} className="col-span-full btn-pill bg-primary text-primary-foreground disabled:opacity-50 mt-2" data-testid="brand-submit">
            {loading ? "Submitting..." : "Join the Waitlist"}
          </button>
        </form>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required, placeholder, col = 1 }) => (
  <div className={col === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
    <input type={type} required={required} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
  </div>
);
const Select = ({ label, value, onChange, options }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3, required, placeholder }) => (
  <div className="sm:col-span-2">
    <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</label>
    <textarea rows={rows} required={required} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-2xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
  </div>
);

export default ForBrandsPage;
